import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import { queryKeys } from "@/api/queryKeys";
import type {
  DataResponse,
  Varianta,
  VariantaCreate,
  VariantaUpdate,
} from "@/api/schemas";

/**
 * Polia s predvoľbou na serveri smú chýbať — nová výroba preberie zisk, DPH,
 * zľavu a réžiu z prvej časti výroby (inak z aktívneho výpočtu), keď ich
 * klient nepošle (KOLO 205/208).
 */
export type NovaVarianta = Pick<VariantaCreate, "nazov"> &
  Partial<VariantaCreate>;

/** Pridaj variantu do zákazky (voliteľne kopíruj z inej). */
export function useCreateVarianta(
  zakazkaId: string,
): UseMutationResult<DataResponse<Varianta>, Error, NovaVarianta> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: NovaVarianta) => {
      const { data } = await api.post<DataResponse<Varianta>>(
        `/zakazky/${zakazkaId}/varianty`,
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

/** PATCH variant nastavení — triggeruje recalc celej zákazky. */
export function useUpdateVarianta(
  zakazkaId: string,
): UseMutationResult<
  DataResponse<Varianta>,
  Error,
  { id: string; data: VariantaUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<Varianta>>(
        `/varianty/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
    },
  });
}

/** Soft delete variant (nie posledná aktívna). */
export function useDeleteVarianta(
  zakazkaId: string,
): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/varianty/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(zakazkaId) });
      // Zmazanie poslednej výrobnej varianty vracia stav (V príprave →
      // Ponuka, KOLO 204) — zoznam a počty stavov by ostali staré
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { VariantaEdgesSummary } from "@/api/schemas";

/** Sumár použitých hrán naprieč variantou (per unikátna hrana).
 * Voliteľné `zostavaId` zúži sumár na jednu zostavu. */
export function useVariantaEdgesSummary(
  variantaId: string | undefined,
  zostavaId?: string,
): UseQueryResult<DataResponse<VariantaEdgesSummary>> {
  return useQuery({
    queryKey: queryKeys.varianty.edgesSummary(variantaId ?? "", zostavaId),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<VariantaEdgesSummary>>(
        `/varianty/${variantaId}/edges-summary`,
        { params: zostavaId ? { zostava_id: zostavaId } : undefined },
      );
      return data;
    },
    enabled: !!variantaId,
  });
}

import type { VariantaKovaniaSummary } from "@/api/schemas";

/** Sumár kovaní naprieč variantou (per unikátne kovanie).
 * Voliteľné `zostavaId` zúži sumár na jednu zostavu. */
export function useVariantaKovaniaSummary(
  variantaId: string | undefined,
  zostavaId?: string,
): UseQueryResult<DataResponse<VariantaKovaniaSummary>> {
  return useQuery({
    queryKey: queryKeys.varianty.kovaniaSummary(variantaId ?? "", zostavaId),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<VariantaKovaniaSummary>>(
        `/varianty/${variantaId}/kovania-summary`,
        { params: zostavaId ? { zostava_id: zostavaId } : undefined },
      );
      return data;
    },
    enabled: !!variantaId,
  });
}

import type { CielovaCena, CielovaCenaVysledok } from "@/api/schemas";

import type { CenaNahlad, CenaNahladVysledok } from "@/api/schemas";

/**
 * Živý náhľad ceny pre pult na marže — hypotéza bez uloženia (KOLO 65).
 *
 * Je to **`useQuery`, nie mutácia**, a hypotéza je súčasťou `queryKey`:
 * pri rýchlom prepisovaní percent („1" → „18" → „185") by pri mutácii
 * mohla vyhrať tá odpoveď, ktorá dobehne posledná, nie tá k poslednému
 * vstupu. TanStack takto sám zahodí staré výsledky a rovnaké hypotézy
 * dedupne. `POST` v query je zámer — endpoint je otázka, nie zmena.
 */
export function useCenaNahlad(
  variantaId: string | undefined,
  hypoteza: CenaNahlad,
  enabled = true,
): UseQueryResult<DataResponse<CenaNahladVysledok>> {
  return useQuery({
    queryKey: queryKeys.varianty.cenaNahlad(variantaId ?? "", hypoteza),
    queryFn: async () => {
      const { data } = await api.post<DataResponse<CenaNahladVysledok>>(
        `/varianty/${variantaId}/cena-nahlad`,
        hypoteza,
      );
      return data;
    },
    enabled: !!variantaId && enabled,
    // Čísla pri prepisovaní nemajú blikať na prázdno
    placeholderData: (predosla) => predosla,
  });
}

/**
 * „Koľko to musí stáť" — z cieľovej ceny spätne zisk %.
 *
 * Endpoint **nič neukladá**; uloženie je bežný `useUpdateVarianta` so
 * vráteným `zisk_pct`. Preto ani tento hook nič neinvaliduje.
 *
 * Počíta to backend zámerne: inverzia stojí na tom istom breakdowne a
 * tom istom zaokrúhľovaní ako reálna cena, takže predpoveď v dialógu
 * **je** to, čo user po uložení uvidí (rovnaký dôvod ako pri
 * `lib/autoEdge.ts` — vzorec prepísaný v TS by sa rozišiel).
 */
export function useCielovaCena(
  variantaId: string | undefined,
): UseMutationResult<DataResponse<CielovaCenaVysledok>, Error, CielovaCena> {
  return useMutation({
    mutationFn: async (payload: CielovaCena) => {
      const { data } = await api.post<DataResponse<CielovaCenaVysledok>>(
        `/varianty/${variantaId}/cielova-cena`,
        payload,
      );
      return data;
    },
  });
}
